import i18n from '@/i18n'
import { formatMoney, tonStringFromPounds, numberWithCommas } from '@/utils/NumericMutations'
import { DataTableColumn, ColumnSet } from '@/model/DataTableColumn.js'
import { sortSettlementInlineRates, sortCorrectionActivities } from '@/utils/SortHelpers'
import { SETTING_KEYS } from '@/utils/UserSettings.js'
import { SettlementColumnContexts } from '@/components/settlements/SettlementKeys.js'

const ifn = (condition, value) => condition ? value : undefined

const settlementsByPayeeHiddenColumnKeys = {
  [SettlementColumnContexts.SettlementsByPayee]: SETTING_KEYS.SETTLEMENTS_BY_PAYEE_HIDDEN_COLUMNS,
  [SettlementColumnContexts.ByproductSettlementsByPayee]: SETTING_KEYS.BP_SETTLEMENTS_BY_PAYEE_HIDDEN_COLUMNS,
  [SettlementColumnContexts.AccrualsByPayee]: SETTING_KEYS.ACCRUALS_BY_PAYEE_HIDDEN_COLUMNS
}
const settlementProductHiddenColumnKeys = {
  [SettlementColumnContexts.SettlementsByContract]: SETTING_KEYS.SETTLEMENTS_BY_CONTRACT_ACTIVITY_HIDDEN_COLUMNS,
  [SettlementColumnContexts.SettlementsByPayee]: SETTING_KEYS.SETTLEMENTS_BY_PAYEE_ACTIVITY_HIDDEN_COLUMNS,
  [SettlementColumnContexts.ByproductSettlementsByPayee]: SETTING_KEYS.BP_SETTLEMENTS_BY_PAYEE_ACTIVITY_HIDDEN_COLUMNS,
  [SettlementColumnContexts.ByproductReceivablesByPayer]: SETTING_KEYS.BP_RECEIVABLES_BY_PAYER_ACTIVITY_HIDDEN_COLUMNS,
  [SettlementColumnContexts.AccrualsByPayee]: SETTING_KEYS.ACCRUALS_BY_PAYEE_ACTIVITY_HIDDEN_COLUMNS
}
const contractTicketHiddenColumnKeys = {
  [SettlementColumnContexts.SettlementsByContract]: SETTING_KEYS.SETTLEMENTS_BY_CONTRACT_TICKET_HIDDEN_COLUMNS,
  [SettlementColumnContexts.SettlementsByPayee]: SETTING_KEYS.SETTLEMENTS_BY_PAYEE_TICKET_HIDDEN_COLUMNS,
  [SettlementColumnContexts.ByproductSettlementsByPayee]: SETTING_KEYS.BP_SETTLEMENTS_BY_PAYEE_TICKET_HIDDEN_COLUMNS,
  [SettlementColumnContexts.ByproductReceivablesByPayer]: SETTING_KEYS.BP_RECEIVABLES_BY_PAYER_TICKET_HIDDEN_COLUMNS,
  [SettlementColumnContexts.AccrualsByPayee]: SETTING_KEYS.ACCRUALS_BY_PAYEE_TICKET_HIDDEN_COLUMNS
}
const bpReceivablesByPayerHiddenColumnKeys = {
  [SettlementColumnContexts.ByproductReceivablesByPayer]: SETTING_KEYS.BP_RECEIVABLES_BY_PAYER_HIDDEN_COLUMNS
}

export function prAdvanceHeaders () {
  return [
    {
      sortable: true,
      text: i18n.t('title'),
      value: 'note'
    },
    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right'
    }
  ]
}

export function prRecoveryHeaders (isExportBatch) {
  return [
    {
      sortable: true,
      text: i18n.t('title'),
      value: 'note',
      align: 'left',
      width: '50%'
    },
    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('balance'),
      value: 'balance',
      align: 'right'
    },
    {
      sortable: true,
      text: isExportBatch ? i18n.t('recoveredInBatch') : i18n.t('recoveries'),
      value: 'recovered',
      align: 'right'
    }
  ]
}

export function paymentRegisterHeaders (isByproduct = false, containsVolume = false) {
  return [
    {
      sortable: true,
      text: i18n.t('activity'),
      value: 'activityName',
      align: 'left',
      width: '20%'
    },

    ifn(!isByproduct, {
      sortable: true,
      text: i18n.t('tract'),
      value: 'tractName'
    }),

    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'weight',
      align: 'right'
    },
    ifn(containsVolume, {
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'volume',
      align: 'right'
    }),
    {
      sortable: true,
      text: i18n.t('amountGross'),
      value: 'payout',
      align: 'right'
    }
  ].filter(f => f !== undefined)
}

export function bpPaymentRegisterHeaders (locale) {
  return [
    {
      sortable: true,
      text: i18n.t('activity'),
      value: 'activityName',
      align: 'left',
      width: '20%'
    },
    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'weight',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('amountGross'),
      value: 'payout',
      align: 'right'
    }
  ].filter(h => h !== undefined)
}

export function bpReceivablesByPayerHeaders (settlementContext = undefined) {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('payer'),
      value: 'counterpartyName',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right'
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('grossTons'),
      value: 'grossTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('averageRateGross'),
      value: 'averageGrossRate',
      align: 'right'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('averageRateNet'),
      value: 'averageNetRate',
      align: 'right'
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('grossAmount'),
      value: 'amount',
      align: 'right',
      hidable: false
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'bpReceivablesByPayerHeaders',
    userSettingKey: bpReceivablesByPayerHiddenColumnKeys[settlementContext]
  })
}

export function receivableRegisterHeaders (isByproduct = false, isDeckActivity = false, containsVolume = false) {
  return [
    {
      sortable: true,
      text: i18n.t('activity'),
      value: 'activityName',
      align: 'left',
      width: '20%'
    },

    ifn(!isByproduct, {
      sortable: true,
      text: i18n.t('tract'),
      value: 'tractName'
    }),
    ifn(isDeckActivity, {
      sortable: true,
      text: i18n.t('location'),
      value: 'locationName'
    }),
    ifn(isDeckActivity, {
      sortable: true,
      text: i18n.t('deck'),
      value: 'fromDeckName'
    }),
    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glCodeOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('loads'),
      value: 'loadCount',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'weight',
      align: 'right'
    },
    ifn(containsVolume, {
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'volume',
      align: 'right'
    }),
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right'
    }
  ].filter(h => h !== undefined)
}

export function registerHeaders (isByproduct = false, containsVolume = false) {
  return [
    {
      sortable: true,
      text: i18n.t('activity'),
      value: 'activityName',
      align: 'left',
      width: '20%'
    },
    ifn(!isByproduct, {
      sortable: true,
      text: i18n.t('tract'),
      value: 'tractName'
    }),
    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glCodeOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'weight',
      align: 'right'
    },
    ifn(containsVolume, {
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'volume',
      align: 'right'
    }),
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right'
    }
  ].filter(h => h !== undefined)
}

export function tractProductionHeaders () {
  return [
    {
      sortable: true,
      text: i18n.t('product'),
      value: 'productName',
      width: '20%'
    },
    {
      sortable: true,
      text: i18n.t('rateSlashTon'),
      value: 'rate',
      align: 'right',
      width: '20%'
    },
    {
      sortable: true,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right',
      width: '20%'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'weight',
      align: 'right',
      width: '20%'
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      align: 'right',
      value: 'amount',
      width: '20%'
    }
  ]
}

export function tractProductionSpeciesHeaders () {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('species'),
      value: 'species',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('ratePerMbf'),
      value: 'rate',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'netMbf',
      align: 'right',
      hidable: false,
      format: (value) => numberWithCommas(value, 3)
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      hidable: false
    })
  ]
  return new ColumnSet({
    columns: columns,
    setName: 'tractProductionSpeciesHeaders'
  })
}

export function tractSummaryHeaders () {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('tract'),
      value: 'tractName',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netWeight',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'netVolume',
      align: 'right'
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'tractSummaryHeaders',
    userSettingKey: SETTING_KEYS.TICKET_REGISTER_TRACT_SUMMARY_HIDDEN_COLUMNS
  })
}

export function contractTicketHeaders (settlementContext = undefined) {
  const isReceivable = settlementContext === SettlementColumnContexts.ByproductReceivablesByPayer
  const isByproduct = isReceivable || settlementContext === SettlementColumnContexts.ByproductSettlementsByPayee
  const isJournalEntry = settlementContext === SettlementColumnContexts.AccrualsByPayee
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('hashSign'),
      value: 'ticketNumber',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('extTicketNum1'),
      value: 'extTicketNumber1',
      align: 'center'
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('extTicketNum2'),
      value: 'extTicketNumber2',
      align: 'center'
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('product'),
      value: 'product',
      hidable: false
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('grossTons'),
      value: 'gross',
      align: 'right'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('defectTons'),
      value: 'defectWeight',
      align: 'right'
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netWeight',
      align: 'right'
    }),
    new DataTableColumn({
      id: 7,
      sortable: true,
      text: i18n.t('grossMbf'),
      value: 'grossVolume',
      align: 'right',
      hidable: !isByproduct
    }),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'netVolume',
      align: 'right',
      hidable: !isByproduct
    }),
    new DataTableColumn({
      id: 9,
      sortable: true,
      text: i18n.t('recovered'),
      value: 'grossAmount',
      align: 'right',
      hidable: !isReceivable && !isJournalEntry
    }),
    new DataTableColumn({
      id: 10,
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 11,
      sortable: false,
      text: i18n.t('actions'),
      value: 'actions',
      align: 'center',
      hidable: false
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'contractTicketHeaders',
    userSettingKey: contractTicketHiddenColumnKeys[settlementContext]
  })
}

export function ticketRegisterContractSummaryHeaders () {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('hashSign'),
      value: 'ticketNumber',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('trailerId'),
      value: 'truck',
      align: 'left'
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('product'),
      value: 'productName',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('weighedInAt'),
      value: 'weighedInAt',
      align: 'center'
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('weighedOutAt'),
      value: 'weighedOutAt',
      align: 'center'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netWeight',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'netVolume',
      align: 'right'
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'ticketRegisterContractSummaryHeaders',
    userSettingKey: SETTING_KEYS.TICKET_REGISTER_CONTRACT_SUMMARY_HIDDEN_COLUMNS
  })
}

export function prCorrectionHeaders ({ isByproduct = false, includeGross = false }) {
  return [
    {
      text: i18n.t('ticketNumber'),
      value: 'ticketNumber',
      align: 'left'
    },
    {
      text: i18n.t('activity'),
      value: 'activity',
      align: 'left'
    },

    ifn(!isByproduct, {
      sortable: true,
      text: i18n.t('tract'),
      value: 'tract'
    }),
    ifn(isByproduct, {
      sortable: true,
      text: i18n.t('fromAccount'),
      value: 'fromAccount',
      align: 'left'
    }),

    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netWeight',
      align: 'right',
      format: tonStringFromPounds
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      format: formatMoney
    },
    ifn(includeGross, {
      sortable: true,
      text: i18n.t('gross'),
      value: 'grossAmount',
      align: 'right',
      format: formatMoney
    })
  ].filter(h => h !== undefined)
}

export function settlementCorrectionDialogHeaders ({ isByproduct = false, includeGross = false }) {
  return [
    {
      text: ''
    },
    {
      text: i18n.t('ticketNumber'),
      value: 'ticketNumber',
      align: 'left'
    },
    {
      text: i18n.t('activity'),
      value: 'activityBundle',
      align: 'left',
      sort: sortCorrectionActivities
    },

    ifn(!isByproduct, {
      sortable: true,
      text: i18n.t('tract'),
      value: 'tract'
    }),
    ifn(isByproduct, {
      sortable: true,
      text: i18n.t('fromAccount'),
      value: 'fromAccount',
      align: 'left'
    }),

    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netWeight',
      align: 'right',
      format: tonStringFromPounds
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      format: formatMoney
    },
    ifn(includeGross, {
      sortable: true,
      text: i18n.t('gross'),
      value: 'grossAmount',
      align: 'right',
      format: formatMoney
    })
  ].filter(h => h !== undefined)
}

export function productSalesAnalysisHeaders (isDeckActivity = false) {
  return [
    {
      text: i18n.t('product'),
      value: 'productName'
    },
    {
      sortable: true,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netTons',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('totalRevenue'),
      value: 'totalRevenue',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('revenuePerTon'),
      value: 'revenuePerTon',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('productionCost'),
      value: 'productionCost',
      align: 'right'
    },
    ifn(!isDeckActivity, {
      sortable: true,
      text: i18n.t('stumpageCost'),
      value: 'stumpageCost',
      align: 'right'
    }),
    {
      sortable: true,
      text: i18n.t('totalCost'),
      value: 'totalCost',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('costPerTon'),
      value: 'costPerTon',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('grossProfit'),
      value: 'grossProfit',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('grossProfitPerTon'),
      value: 'grossProfitPerTon',
      align: 'right'
    }
  ].filter(h => h !== undefined)
}

export function settlementsByContractHeaders () {
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('account'),
      value: 'contractInfo.accountName',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('tract'),
      value: 'contractInfo.tractName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('destination'),
      value: 'contractInfo.destinationName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('setting'),
      value: 'contractInfo.settingName',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('loads'),
      value: 'contractInfo.loads',
      align: 'right'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'contractInfo.netTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('grossTons'),
      value: 'contractInfo.grossTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 7,
      sortable: true,
      text: i18n.t('rateSlashTon'),
      value: 'rate',
      align: 'right',
      columnAdjusterText: i18n.t('rate')
    }),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'contractInfo.netVolume',
      align: 'right'
    }),
    new DataTableColumn({
      id: 9,
      sortable: true,
      text: i18n.t('grossMbf'),
      value: 'contractInfo.grossVolume',
      align: 'right'
    }),
    new DataTableColumn({
      id: 10,
      sortable: true,
      text: i18n.t('grossAmount'),
      value: 'contractInfo.grossAmount',
      align: 'right'
    }),
    new DataTableColumn({
      id: 11,
      sortable: true,
      text: i18n.t('amount'),
      value: 'contractInfo.amount',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 12,
      sortable: true,
      text: i18n.t('status'),
      value: 'contractInfo.status',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 13,
      sortable: false,
      text: i18n.t('actions'),
      value: 'actions',
      align: 'center',
      hidable: false
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'settlementsByContractHeaders',
    userSettingKey: SETTING_KEYS.SETTLEMENTS_BY_CONTRACT_HIDDEN_COLUMNS
  })
}

export function settlementsByPayeeHeaders (includeVolume, settlementContext = undefined) {
  const headers = [
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('payee'),
      value: 'payeeName',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right'
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('grossTons'),
      value: 'grossTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('rateSlashTon'),
      value: 'rate',
      align: 'right',
      sort: sortSettlementInlineRates,
      columnAdjusterText: i18n.t('rate')
    }),
    ifn(includeVolume, new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'netVolume',
      align: 'right'
    })),
    ifn(includeVolume, new DataTableColumn({
      id: 7,
      sortable: true,
      text: i18n.t('grossMbf'),
      value: 'grossVolume',
      align: 'right'
    })),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 9,
      sortable: true,
      text: i18n.t('grossAmount'),
      value: 'grossAmount',
      align: 'right'
    }),
    new DataTableColumn({
      id: 10,
      sortable: true,
      text: i18n.t('status'),
      value: 'status',
      width: '150px',
      align: 'center',
      hidable: false
    }),
    new DataTableColumn({
      id: 11,
      sortable: false,
      text: i18n.t('actions'),
      value: 'actions',
      align: 'center',
      hidable: false
    })
  ].filter(f => f !== undefined)

  return new ColumnSet({
    columns: headers,
    setName: 'settlementsByPayeeHeader',
    userSettingKey: settlementsByPayeeHiddenColumnKeys[settlementContext]
  })
}

export function journalEntrySettlementByPayeeHeaders (includeVolume = false, settlementContext = undefined) {
  const headers = [
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('payee'),
      value: 'payeeName',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right'
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('grossTons'),
      value: 'grossTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('rate'),
      value: 'rate',
      align: 'right',
      sort: sortSettlementInlineRates
    }),
    ifn(includeVolume, new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'netVolume',
      align: 'right'
    })),
    ifn(includeVolume, new DataTableColumn({
      id: 7,
      sortable: true,
      text: i18n.t('grossMbf'),
      value: 'grossVolume',
      align: 'right'
    })),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 9,
      sortable: true,
      text: i18n.t('status'),
      value: 'status',
      width: '150px',
      align: 'center',
      hidable: false
    })
  ].filter(f => f !== undefined)
  headers.forEach(column => column.setCellClass('je-sbp-table-cell'))

  return new ColumnSet({
    columns: headers,
    setName: 'journalEntrySettlementsByPayeeHeader',
    userSettingKey: settlementsByPayeeHiddenColumnKeys[settlementContext]
  })
}

export function settlementProductHeaders (settlementContext = undefined) {
  const isReceivable = settlementContext === SettlementColumnContexts.ByproductReceivablesByPayer
  const isByproduct = isReceivable || settlementContext === SettlementColumnContexts.ByproductSettlementsByPayee
  const isJournalEntry = settlementContext === SettlementColumnContexts.AccrualsByPayee
  const columns = [
    new DataTableColumn({
      id: 0,
      sortable: true,
      text: i18n.t('product'),
      value: 'product',
      align: 'left',
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      sortable: true,
      text: i18n.t('loadCount'),
      value: 'loadCount',
      align: 'right'
    }),
    new DataTableColumn({
      id: 2,
      sortable: true,
      text: i18n.t('grossTons'),
      value: 'grossTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 3,
      sortable: true,
      text: i18n.t('netTons'),
      value: 'netTons',
      align: 'right'
    }),
    new DataTableColumn({
      id: 4,
      sortable: true,
      text: i18n.t('grossMbf'),
      value: 'grossVolume',
      align: 'right',
      hidable: !isByproduct
    }),
    new DataTableColumn({
      id: 5,
      sortable: true,
      text: i18n.t('netMbf'),
      value: 'netVolume',
      align: 'right',
      hidable: !isByproduct
    }),
    new DataTableColumn({
      id: 6,
      sortable: true,
      text: i18n.t('payOnUnit'),
      value: 'payOnUnit',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 7,
      sortable: true,
      text: i18n.t('rate'),
      value: 'averageRate',
      align: 'right',
      hidable: false
    }),
    new DataTableColumn({
      id: 8,
      sortable: true,
      text: i18n.t('recovered'),
      value: 'recovered',
      align: 'right',
      hidable: !isReceivable && !isJournalEntry
    }),
    new DataTableColumn({
      id: 9,
      sortable: true,
      text: i18n.t('penalty'),
      value: 'penaltyAmount',
      align: 'right',
      hidable: isReceivable
    }),
    new DataTableColumn({
      id: 10,
      sortable: true,
      text: i18n.t('amount'),
      value: 'payout',
      align: 'right',
      hidable: false
    })
  ]

  return new ColumnSet({
    columns,
    setName: 'settlementProductHeaders',
    userSettingKey: settlementProductHiddenColumnKeys[settlementContext]
  })
}

export function tractPaymentDialogHeaders () {
  return [
    {
      sortable: true,
      text: i18n.t('title'),
      value: 'paymentNote',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('tract'),
      value: 'tractName',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('activity'),
      value: 'activityName',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right'
    }
  ]
}

export function accountPaymentDialogHeaders ({ includeActions = false }) {
  return [
    {
      sortable: true,
      text: i18n.t('title'),
      value: 'paymentNote',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('activity'),
      value: 'activityName',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('glCode'),
      value: 'glCode',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('glOffset'),
      value: 'glOffset',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right'
    },
    ifn(includeActions, {
      sortable: false,
      text: i18n.t('actions'),
      value: 'actions',
      align: 'center'
    })
  ].filter(h => h)
}

export function recoveryDialogHeaders (isExportBatch) {
  return [
    {
      sortable: true,
      text: i18n.t('entity'),
      value: 'businessEntityName',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('title'),
      value: 'note',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('type'),
      value: 'type',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('rate'),
      value: 'rate',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('previouslyRecovered'),
      value: 'previouslyRecovered',
      align: 'right'
    },
    {
      sortable: true,
      text: isExportBatch ? i18n.t('recoveredInBatch') : i18n.t('recoveries'),
      value: 'recovered',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('balance'),
      value: 'balance',
      align: 'right'
    }
  ]
}

export function advanceDialogHeaders () {
  return [
    {
      sortable: true,
      text: i18n.t('entity'),
      value: 'businessEntityName',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('title'),
      value: 'note',
      align: 'left'
    },
    {
      sortable: true,
      text: i18n.t('amount'),
      value: 'amount',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('remaining'),
      value: 'remainingBalance',
      align: 'right'
    }
  ]
}

export function rateVerificationGroupingKeys (includePayee = true) {
  return [
    {
      id: 0,
      sortable: true,
      text: i18n.t('activity'),
      value: 'activity',
      align: 'left'
    },
    {
      id: 1,
      sortable: true,
      text: i18n.t('product'),
      value: 'product',
      align: 'left'
    },
    ifn(includePayee, {
      id: 2,
      sortable: true,
      text: i18n.t('counterparty'),
      value: 'counterparty',
      align: 'left'
    }),
    {
      id: 3,
      sortable: true,
      text: i18n.t('rate'),
      value: 'rate',
      align: 'right'
    }
  ].filter(h => h !== undefined)
}
